/**
 * Reusable Table component (scrollable body; optional row selection).
 * Virtual scrolling was removed: react-window v2+ is ESM-only and no longer exports FixedSizeList;
 * the old require() path always failed and only produced console noise.
 */

import React, { useMemo, useCallback } from "react";
import Checkbox from "./Checkbox.js";

/**
 * Table component
 * @param {Object} props - Component props
 * @param {Array} props.columns - Column definitions [{ key, label, render, width }]
 * @param {Array} props.data - Table data
 * @param {Function} props.onRowSelect - Callback when row is selected (row, index, checked)
 * @param {Set|Array} props.selectedRows - Set or array of selected row indices
 * @param {boolean} props.selectable - Whether rows are selectable
 * @param {boolean} props.virtualized - Ignored (kept for API compatibility; use maxHeight + scroll)
 * @param {number} props.rowHeight - Unused (kept for API compatibility)
 * @param {number} props.maxHeight - Maximum table body height in pixels
 * @param {string} props.className - Additional CSS classes
 */
const Table = ({
  columns = [],
  data = [],
  onRowSelect,
  selectedRows = new Set(),
  selectable = false,
  virtualized: _virtualized = false,
  rowHeight: _rowHeight = 50,
  maxHeight = 600,
  className = ""
}) => {
  const selectedRowsSet = useMemo(() => {
    if (selectedRows instanceof Set) return selectedRows;
    return new Set(selectedRows);
  }, [selectedRows]);

  const handleRowSelect = useCallback(
    (index, checked) => {
      if (onRowSelect) {
        const row = data[index];
        onRowSelect(row, index, checked);
      }
    },
    [data, onRowSelect]
  );

  const totalWidth = useMemo(() => {
    return columns.reduce((sum, col) => {
      const width = col.width ? parseInt(col.width, 10) : 200;
      return sum + width;
    }, selectable ? 50 : 0);
  }, [columns, selectable]);

  if (data.length === 0) {
    return (
      <div className={`bg-white dark:bg-[#161b22] rounded-lg border border-gray-200 dark:border-[#30363d] ${className}`}>
        <div className="p-8 text-center text-gray-500 dark:text-[#8b949e]">No data available</div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-[#161b22] rounded-lg border border-gray-200 dark:border-[#30363d] overflow-hidden ${className}`}>
      <div className="bg-gray-50 dark:bg-[#0d1117] border-b border-gray-200 dark:border-[#30363d] sticky top-0 z-10">
        <div className="flex items-center" style={{ width: totalWidth || "100%" }}>
          {selectable && (
            <div className="px-4 py-3 flex-shrink-0" style={{ width: 50 }}>
              <Checkbox
                checked={data.length > 0 && data.every((_, index) => selectedRowsSet.has(index))}
                onChange={(e) => {
                  data.forEach((_, index) => {
                    handleRowSelect(index, e.target.checked);
                  });
                }}
              />
            </div>
          )}
          {columns.map((column, index) => (
            <div
              key={column.key || index}
              className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#8b949e] uppercase tracking-wider flex-1"
              style={{ width: column.width || "auto", minWidth: column.minWidth }}
            >
              {column.label || column.key}
            </div>
          ))}
        </div>
      </div>

      <div style={{ maxHeight, overflow: "auto" }}>
        <div>
          {data.map((row, index) => (
            <div
              key={index}
              className={`
                  flex items-center border-b border-gray-200 dark:border-[#30363d]
                  hover:bg-gray-50 dark:hover:bg-[#1c2128]
                  ${selectedRowsSet.has(index) ? "bg-blue-50 dark:bg-blue-900/20" : ""}
                `}
            >
              {selectable && (
                <div className="px-4 py-2 flex-shrink-0">
                  <Checkbox
                    checked={selectedRowsSet.has(index)}
                    onChange={(e) => handleRowSelect(index, e.target.checked)}
                  />
                </div>
              )}
              {columns.map((column, colIndex) => (
                <div
                  key={column.key || colIndex}
                  className="px-4 py-2 flex-1 text-sm text-gray-900 dark:text-[#c9d1d9]"
                  style={{ width: column.width || "auto", minWidth: column.minWidth }}
                >
                  {column.render ? column.render(row, index) : row[column.key] || ""}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Table;
